import { ApiError, refusalWords } from "@/services/api-client";
import type { FeedbackContext } from "@/lib/support/support-client";

/** How a failure is said on screen, and what its report carries. */
export interface Problem {
  /** What failed: "Praxis couldn't load your clients". */
  title: string;
  /** Why, and whose it is, in words the attorney can act on. */
  reason: string;
  /** The ids that trace it, for the bug report; never the server's or a draft's words. */
  report: FeedbackContext;
}

export interface ProblemAsk {
  /** What the screen was working on, as the attorney knows it: "your clients", "that change". */
  what: string;
  /** What it was doing to it. */
  act?: string;
  /** The screen's ids: a draft's, a run's, a file's. */
  report?: FeedbackContext;
}

const OUR_SIDE =
  "The fault is on our side, not in your work. Try again in a moment.";
const OFFLINE =
  "You seem to be offline. Check your connection, then try again.";
const UNREACHED =
  "Praxis couldn't reach the server. Check your connection, then try again.";

/**
 * Whether the request never reached the server: fetch's own failure, in Chrome's, Firefox's and
 * Safari's words. A proxy or a VPN that blocks the API leaves the browser online.
 */
function unreached(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    /failed to fetch|networkerror|load failed/i.test(error.message)
  );
}

function reasonFor(error: unknown, offTheWire: boolean): string {
  const said = refusalWords(error);
  if (said !== null) return said;
  if (typeof navigator !== "undefined" && !navigator.onLine) return OFFLINE;
  return offTheWire ? UNREACHED : OUR_SIDE;
}

/**
 * Say a failure the one way every error screen says it: what failed, why in words the attorney
 * can act on (the API's only where it wrote them for the attorney), and the ids that trace it.
 *
 * An HTTP failure is traced by its request id, status and code, which find its log lines. One
 * that never reached the server is traced by its kind alone, since its message can hold anything.
 */
export function describeProblem(
  error: unknown,
  { what, act = "load", report = {} }: ProblemAsk,
): Problem {
  const offTheWire = unreached(error);
  const trace: FeedbackContext =
    error instanceof ApiError
      ? {
          Request: error.requestId ?? "none",
          Status: String(error.status),
          Code: error.code ?? "none",
        }
      : {
          Error: offTheWire
            ? "network"
            : error instanceof Error
              ? error.name
              : typeof error,
        };
  return {
    title: `Praxis couldn't ${act} ${what}`,
    reason: reasonFor(error, offTheWire),
    report: { Problem: `${act} ${what}`, ...trace, ...report },
  };
}
